/**
 * Moteur du formateur de post LinkedIn : pur, sans React.
 *
 * Le « gras » et l'« italique » d'un post LinkedIn ne sont pas du formatage :
 * ce sont des lettres du bloc Unicode « Symboles mathématiques alphanumériques »
 * (U+1D400 à U+1D7FF), copiées comme du texte ordinaire. Ce bloc ne contient
 * ni lettres accentuées, ni ligatures, ni chiffres en italique. Les accents
 * sont donc reconstitués avec un signe combinant (é = e gras + U+0301) et le
 * reste est laissé tel quel et signalé, jamais caché.
 *
 * Plages (Unicode, bloc Mathematical Alphanumeric Symbols) :
 *   gras            A-Z U+1D400, a-z U+1D41A, 0-9 U+1D7CE
 *   italique        A-Z U+1D434, a-z U+1D44E (le h est U+210E : U+1D455 est un trou)
 *   gras italique   A-Z U+1D468, a-z U+1D482 (pas de chiffres)
 * Les seuils d'avertissement de `analyze` (part de texte stylé, passage long)
 * sont des repères éditoriaux NeverZero, pas des règles de LinkedIn.
 */

export const LINKEDIN_MAX_CHARS = 3000;

export type Style = 'bold' | 'italic' | 'boldItalic' | 'plain';

const UPPER = 0x41;
const LOWER = 0x61;
const DIGIT = 0x30;

interface Block {
  upper: number;
  lower: number;
  digit: number | null;
}

const BLOCKS: Record<Exclude<Style, 'plain'>, Block> = {
  bold: { upper: 0x1d400, lower: 0x1d41a, digit: 0x1d7ce },
  italic: { upper: 0x1d434, lower: 0x1d44e, digit: null },
  boldItalic: { upper: 0x1d468, lower: 0x1d482, digit: null },
};

const ITALIC_H = 0x210e;
const ITALIC_H_HOLE = 0x1d455;

/** Table inverse : caractère stylé -> caractère ASCII d'origine. */
const REVERSE = new Map<number, string>();
for (const block of Object.values(BLOCKS)) {
  for (let i = 0; i < 26; i++) {
    REVERSE.set(block.upper + i, String.fromCharCode(UPPER + i));
    REVERSE.set(block.lower + i, String.fromCharCode(LOWER + i));
  }
  if (block.digit !== null) {
    for (let i = 0; i < 10; i++) REVERSE.set(block.digit + i, String.fromCharCode(DIGIT + i));
  }
}
REVERSE.set(ITALIC_H, 'h');

export function isStyledChar(ch: string): boolean {
  const cp = ch.codePointAt(0);
  return cp !== undefined && REVERSE.has(cp);
}

/** Remet un texte en lettres ordinaires (les accents combinés se recomposent). */
export function toPlain(text: string): string {
  let out = '';
  for (const ch of text) out += REVERSE.get(ch.codePointAt(0) as number) ?? ch;
  return out.normalize('NFC');
}

function mapAscii(ch: string, style: Exclude<Style, 'plain'>): string | null {
  const block = BLOCKS[style];
  const cp = ch.charCodeAt(0);
  if (cp >= 0x41 && cp <= 0x5a) return String.fromCodePoint(block.upper + cp - UPPER);
  if (cp >= 0x61 && cp <= 0x7a) {
    if (style === 'italic' && cp === 0x68) return String.fromCodePoint(ITALIC_H);
    return String.fromCodePoint(block.lower + cp - LOWER);
  }
  if (cp >= 0x30 && cp <= 0x39 && block.digit !== null) return String.fromCodePoint(block.digit + cp - DIGIT);
  return null;
}

export interface ApplyResult {
  text: string;
  /** Lettres accentuées converties avec un signe combinant. */
  accented: number;
  /** Caractères qui n'ont pas de version stylée (chiffres en italique, œ, ß...), tels quels. */
  skipped: string[];
}

/** Applique un style à un passage. Un passage déjà stylé est d'abord remis à plat. */
export function styleText(segment: string, style: Style): ApplyResult {
  const plain = toPlain(segment);
  if (style === 'plain') return { text: plain, accented: 0, skipped: [] };

  let out = '';
  let accented = 0;
  const skipped = new Set<string>();
  for (const ch of plain) {
    const direct = mapAscii(ch, style);
    if (direct) {
      out += direct;
      continue;
    }
    const decomposed = ch.normalize('NFD');
    const base = decomposed[0];
    if (decomposed.length > 1 && /^[A-Za-z]$/.test(base) && /^\p{M}+$/u.test(decomposed.slice(1))) {
      const mapped = mapAscii(base, style);
      if (mapped) {
        out += mapped + decomposed.slice(1);
        accented++;
        continue;
      }
    }
    out += ch;
    if (/[\p{L}\p{N}]/u.test(ch)) skipped.add(ch);
  }
  return { text: out, accented, skipped: [...skipped] };
}

/** Décale une borne de sélection pour ne jamais couper une paire de substitution. */
function snap(text: string, index: number, forward: boolean): number {
  const lo = text.charCodeAt(index - 1);
  const hi = text.charCodeAt(index);
  const splits = lo >= 0xd800 && lo <= 0xdbff && hi >= 0xdc00 && hi <= 0xdfff;
  return splits ? index + (forward ? 1 : -1) : index;
}

export interface ApplyToSelection extends ApplyResult {
  /** Bornes (indices UTF-16) du passage après transformation, pour restaurer la sélection. */
  start: number;
  end: number;
}

export function applyToSelection(text: string, start: number, end: number, style: Style): ApplyToSelection {
  const from = snap(text, Math.max(0, Math.min(start, end)), false);
  const to = snap(text, Math.min(text.length, Math.max(start, end)), true);
  const result = styleText(text.slice(from, to), style);
  return {
    ...result,
    text: text.slice(0, from) + result.text + text.slice(to),
    start: from,
    end: from + result.text.length,
  };
}

/** Première ligne non vide : [début, fin) en indices UTF-16, ou null. */
export function firstLineRange(text: string): [number, number] | null {
  const match = /\S[^\n]*/.exec(text);
  if (!match) return null;
  const end = match.index + match[0].replace(/\s+$/, '').length;
  return [match.index, end];
}

export type Severity = 'problem' | 'warning' | 'ok';

export interface Finding {
  id: string;
  severity: Severity;
  title: string;
  detail: string;
  fix?: string;
}

export interface Analysis {
  stats: { chars: number; styled: number; share: number; longestRun: number };
  findings: Finding[];
}

/** Longueur en points de code ; les signes combinants comptent chacun comme un caractère (estimation). */
export function countChars(text: string): number {
  return Array.from(text).length;
}

const STYLED_RUN = (() => {
  const parts = [...REVERSE.keys()].map((cp) => `\\u{${cp.toString(16)}}`);
  return new RegExp(`(?:[${parts.join('')}]\\p{M}*)+`, 'gu');
})();

export function analyze(text: string): Analysis {
  const chars = countChars(text);
  const runs = [...text.matchAll(STYLED_RUN)];
  const styled = runs.reduce((sum, m) => sum + countChars(m[0]), 0);
  const longestRun = runs.reduce((max, m) => Math.max(max, countChars(m[0])), 0);
  const share = chars > 0 ? styled / chars : 0;
  const findings: Finding[] = [];

  if (chars > LINKEDIN_MAX_CHARS) {
    findings.push({
      id: 'length',
      severity: 'problem',
      title: `${chars - LINKEDIN_MAX_CHARS} caractères au-dessus de la limite`,
      detail: `Un post LinkedIn plafonne à ${LINKEDIN_MAX_CHARS} caractères. Chaque accent combiné ajoute un caractère, ce qui pèse plus vite qu'il n'y paraît dans un texte en français.`,
      fix: 'Raccourcissez le texte avant de publier.',
    });
  }

  const hashtagStyled = runs.some((m) => m.index !== undefined && text[m.index - 1] === '#');
  if (hashtagStyled) {
    findings.push({
      id: 'hashtag',
      severity: 'problem',
      title: 'Un hashtag est en gras ou en italique',
      detail: 'Le hashtag n\'est plus fait des lettres ordinaires : LinkedIn ne le reconnaîtra très probablement pas comme un hashtag cliquable.',
      fix: 'Remettez les hashtags en texte normal.',
    });
  }

  const urlStyled = [...text.matchAll(/https?:\/\/\S+|www\.\S+/g)].some((m) => [...m[0]].some(isStyledChar));
  if (urlStyled) {
    findings.push({
      id: 'url',
      severity: 'problem',
      title: 'Un lien contient des lettres stylées',
      detail: 'Une adresse écrite avec des caractères mathématiques ne mène nulle part : le lien est cassé.',
      fix: 'Remettez le lien en texte normal.',
    });
  }

  const accentedStyled = runs.reduce((sum, m) => sum + (m[0].match(/\p{M}/gu)?.length ?? 0), 0);
  if (accentedStyled > 0) {
    findings.push({
      id: 'accents',
      severity: 'warning',
      title: `${accentedStyled} accent${accentedStyled > 1 ? 's' : ''} recomposé${accentedStyled > 1 ? 's' : ''} sur du texte stylé`,
      detail: 'Le bloc Unicode du gras n\'a pas de lettre accentuée : l\'accent est un signe séparé posé sur la lettre. Selon la police et l\'appareil, il peut se décaler ou disparaître.',
      fix: 'Vérifiez l\'aperçu sur votre téléphone, ou gardez en gras des mots sans accent.',
    });
  }

  if (longestRun > 80) {
    findings.push({
      id: 'long-run',
      severity: 'warning',
      title: `Un passage stylé de ${longestRun} caractères`,
      detail: 'Un long passage en caractères mathématiques se lit mal : les lecteurs d\'écran peuvent épeler chaque lettre, et l\'œil fatigue vite sur une police de repli.',
      fix: 'Limitez le gras à un titre ou à quelques mots.',
    });
  }

  if (chars >= 100 && share > 0.3) {
    findings.push({
      id: 'share',
      severity: 'warning',
      title: `${Math.round(share * 100)} % du texte est stylé`,
      detail: 'Quand presque tout est en gras, plus rien ne ressort. Ce texte stylé n\'est de plus pas retrouvé par la recherche, qui ne voit pas de mots dedans (repère NeverZero : environ 30 % maximum).',
      fix: 'Gardez le style pour les 1 à 3 éléments que le lecteur doit retenir.',
    });
  }

  if (styled === 0) {
    findings.push({
      id: 'none',
      severity: 'ok',
      title: 'Aucun passage stylé pour l\'instant',
      detail: 'Sélectionnez des mots dans le texte, puis choisissez un style.',
    });
  } else if (findings.length === 0) {
    findings.push({
      id: 'clean',
      severity: 'ok',
      title: 'Le style reste mesuré',
      detail: `${styled} caractères stylés sur ${chars} (${Math.round(share * 100)} %), sans hashtag ni lien touché.`,
    });
  }

  return { stats: { chars, styled, share, longestRun }, findings };
}
